import * as React from 'react';
import { Box, styled } from '@mui/material';
import Done from '@mui/icons-material/Done';
import Close from '@mui/icons-material/Close';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';
import { FormikValues } from 'formik';

import { MuiCardForm } from 'lib/form/MuiCardForm';
import { SettingDisplay } from 'lib/SettingDisplay';
import { Option } from 'lib/types';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { SettingDisplayPassword } from '../SettingDisplayPassword';
import { isUndefined } from 'lib/typeguards';

export enum DeliveryProvider {
    UBER_DIRECT = 'UBER_DIRECT'
}

const deliveryProviderOptions: Option[] = [
    { label: 'None', value: null },
    { label: 'Uber Direct', value: DeliveryProvider.UBER_DIRECT }
];

const PREFIX = 'DeliverySettings';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2)
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    }
}));

type IProps = {
    settings: IPublicAndPrivateSettings;
    locationId: string;
    onSubmit: (values: Record<string, any>) => Promise<boolean>;
};

export const DeliverySettings = ({ settings, locationId, onSubmit }: IProps) => {
    const initialValues = React.useMemo(
        () => ({
            deliveryProvider: settings?.deliveryProvider,
            uberDirectCustomerId: settings?.uberDirect?.customerId,
            uberDirectClientId: settings?.uberDirect?.clientId,
            uberDirectClientSecret: settings?.uberDirect?.clientSecret,
            uberDirectWebhookSecret: settings?.uberDirect?.webhookSecret,
            capturePhoneNumberOnOrder_DELIVERY: settings?.capturePhoneNumberOnOrder?.DELIVERY ?? false,
            activeOrderDurationPaddingMins: settings?.delivery?.activeOrderDurationPaddingMins,
            standardPrepTimeMins: settings?.orderPreparationTimeMins?.DELIVERY?.standard,
            quietPrepTimeMins: settings?.orderPreparationTimeMins?.DELIVERY?.quiet,
            busyPrepTimeMins: settings?.orderPreparationTimeMins?.DELIVERY?.busy,
            deliveryBaseFare: settings?.delivery?.baseFare,
            deliveryBaseDistance: settings?.delivery?.baseDistance,
            deliveryCostPerDistance: settings?.delivery?.costPerDistance,
            deliveryDistanceInFullIncrements: settings?.delivery?.distanceInFullIncrements ?? false,
            deliveryDistanceIncrement: settings?.delivery?.distanceIncrement,
            deliveryDistanceUnit: settings?.delivery?.distanceUnit,
            deliveryMinimumBasketAmount: settings?.delivery?.minimumBasketAmount,
            deliveryMinimumBasketAmountFee: settings?.delivery?.minimumBasketAmountFee,
            deliveryChargePercentage: settings?.delivery?.chargePercentage,
            deliveryMaximumChargeValue: settings?.delivery?.maximumChargeValue
        }),
        [settings]
    );

    const standardPrepTimeDisplay = React.useMemo(() => {
        if (locationId !== undefined) {
            return (
                settings?.orderPreparationTimeMins?.DELIVERY?.standard ??
                'Unset (currently uses GLOBAL setting)'
            );
        }
        return settings?.orderPreparationTimeMins?.DELIVERY?.standard;
    }, [locationId, settings.orderPreparationTimeMins?.DELIVERY?.standard]);

    const quietPrepTimeDisplay = React.useMemo(() => {
        if (locationId !== undefined) {
            return (
                settings?.orderPreparationTimeMins?.DELIVERY?.quiet ?? 'Unset (currently uses GLOBAL setting)'
            );
        }
        return settings?.orderPreparationTimeMins?.DELIVERY?.quiet;
    }, [locationId, settings.orderPreparationTimeMins?.DELIVERY?.quiet]);

    const busyPrepTimeDisplay = React.useMemo(() => {
        if (locationId !== undefined) {
            return (
                settings?.orderPreparationTimeMins?.DELIVERY?.busy ?? 'Unset (currently uses GLOBAL setting)'
            );
        }
        return settings?.orderPreparationTimeMins?.DELIVERY?.busy;
    }, [locationId, settings.orderPreparationTimeMins?.DELIVERY?.busy]);
    const capturePhoneNumberOnOrderDisplay = React.useMemo(() => {
        if (locationId !== undefined) {
            const isUnset = isUndefined(settings?.capturePhoneNumberOnOrder?.DELIVERY);
            if (isUnset) {
                return 'Unset (currently uses GLOBAL setting)';
            }
        }
        // Global setting
        return settings?.capturePhoneNumberOnOrder?.DELIVERY ? <Done /> : <Close />;
    }, [locationId, settings?.capturePhoneNumberOnOrder?.DELIVERY]);

    const renderDeliveryEdit = React.useCallback(
        (values: FormikValues) => (
            <Box display="flex" flexDirection="column">
                <SettingDisplay label="Standard Preparation Time" description="The duration is in minutes">
                    <TextFormField type="number" name="standardPrepTimeMins" />
                </SettingDisplay>
                <SettingDisplay label="Quiet Preparation Time" description="The duration is in minutes">
                    <TextFormField type="number" name="quietPrepTimeMins" />
                </SettingDisplay>
                <SettingDisplay label="Busy Preparation Time" description="The duration is in minutes">
                    <TextFormField type="number" name="busyPrepTimeMins" />
                </SettingDisplay>
                {locationId === undefined && (
                    <>
                        <SettingDisplay label="Delivery Provider">
                            <SelectFormField
                                name="deliveryProvider"
                                emptyLabel="None"
                                displayEmpty
                                options={deliveryProviderOptions}
                            />
                        </SettingDisplay>
                        {values?.deliveryProvider === DeliveryProvider.UBER_DIRECT && (
                            <>
                                <SettingDisplay label="Customer ID">
                                    <TextFormField name="uberDirectCustomerId" />
                                </SettingDisplay>
                                <SettingDisplay label="Client ID">
                                    <TextFormField name="uberDirectClientId" />
                                </SettingDisplay>
                                <SettingDisplay label="Client Secret">
                                    <TextFormField name="uberDirectClientSecret" />
                                </SettingDisplay>
                                <SettingDisplay label="Webhook Secret">
                                    <TextFormField name="uberDirectWebhookSecret" />
                                </SettingDisplay>
                            </>
                        )}
                        <SettingDisplay
                            label="Active Order Duration Padding"
                            description="The duration is in minutes"
                        >
                            <TextFormField type="number" name="activeOrderDurationPaddingMins" />
                        </SettingDisplay>
                        <SettingDisplay label="Base Fare">
                            <TextFormField type="number" name="deliveryBaseFare" />
                        </SettingDisplay>
                        <SettingDisplay label="Base Distance">
                            <TextFormField type="number" name="deliveryBaseDistance" />
                        </SettingDisplay>
                        <SettingDisplay label="Cost Per Distance">
                            <TextFormField type="number" name="deliveryCostPerDistance" />
                        </SettingDisplay>
                        <SettingDisplay label="Distance In Full Increments">
                            <SwitchFormField name="deliveryDistanceInFullIncrements" />
                        </SettingDisplay>
                        <SettingDisplay label="Distance Increments">
                            <TextFormField type="number" name="deliveryDistanceIncrement" />
                        </SettingDisplay>
                        <SettingDisplay label="Distance Unit">
                            <TextFormField name="deliveryDistanceUnit" />
                        </SettingDisplay>
                        <SettingDisplay label="Minimum Basket Amount">
                            <TextFormField type="number" name="deliveryMinimumBasketAmount" />
                        </SettingDisplay>
                        <SettingDisplay label="Minimum Basket Amount Fee">
                            <TextFormField type="number" name="deliveryMinimumBasketAmountFee" />
                        </SettingDisplay>
                        <SettingDisplay label="Charge Percentage">
                            <TextFormField type="number" name="deliveryChargePercentage" />
                        </SettingDisplay>
                        <SettingDisplay label="Maximum Charge Value">
                            <TextFormField type="number" name="deliveryMaximumChargeValue" />
                        </SettingDisplay>
                    </>
                )}
                {/* Common */}
                <SettingDisplay label="Capture Phone Number on Delivery">
                    <SwitchFormField name="capturePhoneNumberOnOrder_DELIVERY" />
                </SettingDisplay>
            </Box>
        ),
        [locationId]
    );
    return (
        <StyledMuiCardForm
            title="Delivery"
            initialValues={initialValues}
            onSubmit={onSubmit}
            childrenWhileDisplaying={
                <Box display="flex" flexDirection="column">
                    <SettingDisplay
                        label="Standard Preparation Time"
                        description="The duration is in minutes"
                    >
                        {standardPrepTimeDisplay}
                    </SettingDisplay>
                    <SettingDisplay label="Quiet Preparation Time" description="The duration is in minutes">
                        {quietPrepTimeDisplay}
                    </SettingDisplay>
                    <SettingDisplay label="Busy Preparation Time" description="The duration is in minutes">
                        {busyPrepTimeDisplay}
                    </SettingDisplay>
                    {locationId === undefined && (
                        <>
                            <SettingDisplay label="Delivery Provider">
                                {deliveryProviderOptions.find(
                                    option => option.value === settings?.deliveryProvider
                                )?.label ?? 'None'}
                            </SettingDisplay>
                            {settings?.deliveryProvider === DeliveryProvider.UBER_DIRECT && (
                                <>
                                    <SettingDisplay label="Customer ID">
                                        {settings?.uberDirect?.customerId}
                                    </SettingDisplay>
                                    <SettingDisplay label="Client ID">
                                        {settings?.uberDirect?.clientId}
                                    </SettingDisplay>
                                    <SettingDisplayPassword
                                        title="Client Secret"
                                        text={settings?.uberDirect?.clientSecret}
                                    />
                                    <SettingDisplayPassword
                                        title="Webhook Secret"
                                        text={settings?.uberDirect?.webhookSecret}
                                    />
                                </>
                            )}
                            <SettingDisplay
                                label="Active Order Duration Padding"
                                description="The duration is in minutes"
                            >
                                {settings?.delivery?.activeOrderDurationPaddingMins}
                            </SettingDisplay>
                            <SettingDisplay label="Base Fare">{settings?.delivery?.baseFare}</SettingDisplay>
                            <SettingDisplay label="Base Distance">
                                {settings?.delivery?.baseDistance}
                            </SettingDisplay>
                            <SettingDisplay label="Cost Per Distance">
                                {settings?.delivery?.costPerDistance}
                            </SettingDisplay>
                            <SettingDisplay label="Distance In Full Increments">
                                {settings?.delivery?.distanceInFullIncrements ? <Done /> : <Close />}
                            </SettingDisplay>
                            <SettingDisplay label="Distance Increments">
                                {settings?.delivery?.distanceIncrement}
                            </SettingDisplay>
                            <SettingDisplay label="Distance Unit">
                                {settings?.delivery?.distanceUnit}
                            </SettingDisplay>
                            <SettingDisplay label="Minimum Basket Amount">
                                {settings?.delivery?.minimumBasketAmount}
                            </SettingDisplay>
                            <SettingDisplay label="Minimum Basket Amount Fee">
                                {settings?.delivery?.minimumBasketAmountFee}
                            </SettingDisplay>
                            <SettingDisplay label="Charge Percentage">
                                {settings?.delivery?.chargePercentage}
                            </SettingDisplay>
                            <SettingDisplay label="Maximum Charge Value">
                                {settings?.delivery?.maximumChargeValue}
                            </SettingDisplay>
                        </>
                    )}
                    {/* Common */}
                    <SettingDisplay label="Capture Phone Number on Delivery">
                        {capturePhoneNumberOnOrderDisplay}
                    </SettingDisplay>
                </Box>
            }
            childrenWhileEditing={renderDeliveryEdit}
            className={classes.settingsCard}
            boxClassName={classes.displayClassName}
        />
    );
};
